import React, { useEffect, useState, useRef } from "react";
import Chart from "react-apexcharts";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import ZoomModal from "./zoomModal";
import { divisionLevelHeatMap } from "../../services/api-services";
import generateChartImage from "../../admin/components/Charts/generateChartImage";
import Modal from "../../admin/components/common/Model";
import { toastEmitter } from "../../utills/utilities";
import { CHAT_ICON, MENU_ICON, ZOOM_ICON } from "../../utills/image-constant";
import Remark from "../../admin/components/Charts/Remark";

const DivisionHeatmap = ({
  selectedCompanyId,
  selectedCampaignId,
  type,
  graphName,
  title = "Demographic Dashboard – Division",
}) => {
  const [divisionData, setDivisionData] = useState([]);
  const [remark, setRemark] = useState("");
  const [remarkId, setRemarkId] = useState(null);
  const [isAttempt, setIsAttempt] = useState();
  const [colorRanges, setColorRanges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showRemarkModal, setShowRemarkModal] = useState(false);
  const [remarkText, setRemarkText] = useState("");
  const modalRef = useRef(null);
  const chartRef = useRef(null);
  const modalId = "divisionHeatmapZoom";

  //  Fetch Heatmap Data
  const getHeatmapData = async () => {
    setLoading(true);
    try {
      const response = await divisionLevelHeatMap(
        selectedCompanyId,
        selectedCampaignId,
        "department",
        type,
        graphName
      );
      if (response?.data?.responseCode === 200) {
        const { remark = "", remarkId = null, isAttempt = 0 } = response.data;
        setRemarkText(remark);
        setRemarkId(remarkId);
        setIsAttempt(isAttempt);
        setDivisionData(response?.data?.responseBody || []);
        setColorRanges(response?.data?.colorList || []);
      }
    } catch (err) {
      console.error(err);
      toastEmitter("error", "Error fetching data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedCompanyId) getHeatmapData();
  }, [selectedCompanyId]);

  //  Prepare chart data
  const categories = [];
  let data = [];

  if (divisionData?.length > 0) {
    divisionData.forEach((item) => {
      item.dimensionOverallHeatmapChartResponseList?.forEach((dimension) => {
        if (!categories.includes(dimension.dimensionName)) {
          categories.push(dimension.dimensionName);
        }

        const entry = data.find((d) => d.name === item.name);
        if (entry) {
          entry.values.push(parseFloat(dimension.percentage.toFixed(2)));
        } else {
          data.push({
            name: item.name,
            values: [parseFloat(dimension.percentage.toFixed(2))],
          });
        }
      });
    });
  }

  //  Grand Total
  const grandTotal = categories.map((_, idx) => {
    const sum = data.reduce((total, row) => total + (row.values[idx] || 0), 0);
    const average = sum / data.length;
    return parseFloat(average.toFixed(2));
  });

  // Get color dynamically based on API color ranges
  const getColorForValue = (value) => {
    for (let i = 0; i < colorRanges.length; i++) {
      const range = colorRanges[i];
      if (value >= range.rangeFrom && value <= range.rangeTo) {
        return range.colorName;
      }
    }
    return "#D3D3D3"; // default light gray if no match
  };

  const grandTotalColor =
    grandTotal.length > 0 ? getColorForValue(grandTotal[0]) : "#000000";

  const series = [
    ...data.map((row) => ({
      name: row.name,
      data: row.values,
    })),
    {
      name: "Grand Total (Average)",
      data: grandTotal,
      color: grandTotalColor,
    },
  ];

  //  Dynamic color scale
  const colorScaleRanges = colorRanges.map((range) => ({
    from: range.rangeFrom,
    to: range.rangeTo,
    color: range.colorName,
    name: `${range.rangeFrom}% - ${range.rangeTo}%`,
  }));

  //  Chart options
  const options = {
    chart: {
      id: "demographic_dashboard_division",
      type: "heatmap",
      toolbar: { show: false },
      events: {
        mounted: () => {
          setTimeout(() => {
            generateChartImage(
              "demographic_dashboard_division",
              "demographic_dashboard_division",
              remark
            );
          }, 100);
        },
      },
    },
    plotOptions: {
      heatmap: {
        colorScale: {
          ranges: colorScaleRanges,
        },
      },
    },
    xaxis: {
      categories,
      labels: {
        style: { fontSize: "12px", fontWeight: "bold" },
      },
      position: "top",
    },
    yaxis: {
      labels: {
        style: { fontSize: "12px", fontWeight: "bold" },
        formatter: (value) =>
          value === "Grand Total (Average)" ? "Grand Total" : value,
      },
      reversed: true,
    },
    dataLabels: {
      enabled: true,
      formatter: (value) => `${value}%`,
      style: { fontSize: "10px", fontWeight: "bold", colors: ["#000"] },
    },
    tooltip: {
      enabled: true,
      y: {
        formatter: (value) => `${value}%`,
      },
    },
    legend: { show: false }, // We'll render our own legend below
  };

  //  Export functions (Excel / Image / PDF)
  const exportAsExcel = () => {
    if (!divisionData || !Array.isArray(divisionData)) return;
    const headers = ["Department Name", "Dimension Name", "Percentage"];
    const rows = [];
    const percentageValues = [];

    divisionData.forEach((role) => {
      role.dimensionOverallHeatmapChartResponseList?.forEach((dimension) => {
        rows.push([role.name, dimension.dimensionName, dimension.percentage]);
        percentageValues.push(dimension.percentage);
      });
    });

    const total = percentageValues.reduce((acc, val) => acc + val, 0);
    const average = percentageValues.length ? total / percentageValues.length : 0;
    rows.push(["", "Grand Total (Average)", average.toFixed(2)]);

    const worksheet = XLSX.utils.aoa_to_sheet([
      [title],
      [],
      headers,
      ...rows,
    ]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Division Summary");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `${title}.xlsx`
    );
  };

  const exportAsImage = async () => {
    const chartElement = chartRef.current?.container;
    if (!chartElement) return;
    const canvas = await html2canvas(chartElement);
    const imgData = canvas.toDataURL("image/png");
    const link = document.createElement("a");
    link.href = imgData;
    link.download = `${title}.png`;
    link.click();
  };

  const exportAsPDF = async () => {
    const chartElement = chartRef.current?.container;
    if (!chartElement) return;
    const canvas = await html2canvas(chartElement);
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF({ orientation: "landscape" });
    const imgWidth = 280;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    pdf.addImage(imgData, "PNG", 10, 10, imgWidth, imgHeight);
    pdf.save(`${title}.pdf`);
  };

  return (
    <div className="card h-100 ">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center overall-text mb-2 p-2">
        <h5 className="m-0">{title}</h5>
        <div className="d-flex align-items-center">
          <button
            className="bg-transparent border-0 me-2"
            data-bs-toggle="modal"
            data-bs-target={`#${modalId}`}
          >
            <img src={ZOOM_ICON} alt="zoom" />
          </button>
          <div className="dropdown">
            <button
              className="icon-btn bg-transparent"
              type="button"
              data-bs-toggle="dropdown"
            >
              <img src={MENU_ICON} alt="menu" />
            </button>
            <ul className="dropdown-menu dropdown-menu-end">
              <li>
                <button className="dropdown-item" onClick={exportAsExcel}>
                  Download Excel
                </button>
              </li>
              <li>
                <button className="dropdown-item" onClick={exportAsPDF}>
                  Export PDF
                </button>
              </li>
              <li>
                <button className="dropdown-item" onClick={exportAsImage}>
                  Download Image
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Chart */}
      {loading ? (
        <div className="text-center py-5">Loading...</div>
      ) : (
        <div
          className="chart10"
          data-heading={title}
          data-remark={remark}
        >
          <Chart
            ref={chartRef}
            options={options}
            series={series}
            type="heatmap"
            height={400}
          />
        </div>
      )}

      {/* ✅ Dynamic Legend */}
      {colorRanges.length > 0 && (
        <div className="d-flex flex-wrap justify-content-center align-items-center gap-3 mt-3">
          {colorRanges.map((range, idx) => (
            <div key={idx} className="d-flex align-items-center">
              <div
                style={{
                  width: "20px",
                  height: "20px",
                  backgroundColor: range.colorName,
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  marginRight: "8px",
                }}
              ></div>
              <span className="text-muted fw-semibold">
                {range.rangeFrom}% - {range.rangeTo}%
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Chat/Remark Button */}
      <div className="d-flex justify-content-end my-2">
        <button
          title="Add Comment"
          onClick={() => {
            setShowRemarkModal(true);
            setTimeout(() => {
              if (modalRef.current) modalRef.current.click();
            }, 0);
          }}
          className="border-0 bg-transparent"
        >
          <img src={CHAT_ICON} alt="chat" />
        </button>
      </div>

      {/* Remark Modal */}
      {showRemarkModal && (
        <Modal modalClick={modalRef} title="Remark" buttonLable="">
          <Remark
           selectedCompanyId={selectedCompanyId}
            selectedCampaignId={selectedCampaignId}
            type={type}
            graphName={graphName}
            initialRemark={remarkText}
            remarkId={remarkId}
            isAttempt={0}
            setRemarkprops={setRemarkText}
            setRemarkIdProps={setRemarkId}
            onCloseModal={() => setShowRemarkModal(false)}
          />
        </Modal>
      )}

      {/* Zoom Modal */}
      <ZoomModal
        id={modalId}
        title={title}
        chartOptions={options}
        seriesData={series}
        type="heatmap"
        height={400}
           remarkText={remarkText}
        setRemarkText={setRemarkText}
        showRemarkModal={showRemarkModal}
        setShowRemarkModal={setShowRemarkModal}
        modalRef={modalRef}
        exportSummaryToExcel={exportAsExcel}
        exportAsPDF={exportAsPDF}
        exportAsImage={exportAsImage}
      />
    </div>
  );
};

export default DivisionHeatmap;
